import { CustomPartner, CreateCustomPartnerRequest, UpdateCustomPartnerRequest } from '../types/customPartners';
import { getToken } from '../utils/auth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const API_BASE_URL = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      console.error('Unauthorized! Token may be invalid or expired');
      // Optionally redirect to login or refresh token
    }
    
    // Try to get error message from response
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    
    throw new Error(errorMessage);
  }

  // Handle empty responses (like DELETE)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return null;
};

export const customPartnersApi = {
  // Get all custom partners for the current user
  getAll: async (includeInactive = false): Promise<CustomPartner[]> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners?includeInactive=${includeInactive}`);
      // Backend returns { success, message, data } wrapper
      return response?.data || [];
    } catch (error) {
      console.error('Error fetching custom partners:', error);
      throw error;
    }
  },

  // Get custom partners by type for the current user
  getByType: async (partnerType: string, includeInactive = false): Promise<CustomPartner[]> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/by-type/${encodeURIComponent(partnerType)}?includeInactive=${includeInactive}`);
      // Backend returns { success, message, data } wrapper
      return response?.data || [];
    } catch (error) {
      console.error('Error fetching custom partners by type:', error);
      throw error;
    }
  },

  // Get custom partner by ID
  getById: async (id: number): Promise<CustomPartner> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/${id}`);
      // Backend returns { success, message, data } wrapper
      if (!response?.data) {
        throw new Error('Partner not found');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching custom partner:', error);
      throw error;
    }
  },

  // Create new custom partner
  create: async (partner: CreateCustomPartnerRequest): Promise<CustomPartner> => {
    try {
      const response = await makeAuthenticatedRequest('/CustomPartners', {
        method: 'POST',
        body: JSON.stringify(partner),
      });

      // Backend returns { success, message, data } wrapper
      if (!response?.data) {
        throw new Error('Failed to create partner');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating custom partner:', error);
      throw error;
    }
  },

  // Update existing custom partner
  update: async (id: number, partner: UpdateCustomPartnerRequest): Promise<CustomPartner> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/${id}`, {
        method: 'PUT',
        body: JSON.stringify(partner),
      });

      // Backend returns { success, message, data } wrapper
      if (!response?.data) {
        throw new Error('Failed to update partner');
      }

      return response.data;
    } catch (error) {
      console.error('Error updating custom partner:', error);
      throw error;
    }
  },

  // Delete custom partner
  delete: async (id: number): Promise<void> => {
    try {
      await makeAuthenticatedRequest(`/CustomPartners/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting custom partner:', error);
      throw error;
    }
  },

  // Toggle partner active status
  toggleActive: async (id: number): Promise<CustomPartner> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/${id}/toggle-active`, {
        method: 'PATCH',
      });

      // Backend returns { success, message, data } wrapper
      if (!response?.data) {
        throw new Error('Failed to toggle partner status');
      }

      return response.data;
    } catch (error) {
      console.error('Error toggling partner status:', error);
      throw error;
    }
  },

  // Get partner types (for dropdown/filter options)
  getPartnerTypes: async (): Promise<Array<{ value: string; label: string; icon: string; color: string }>> => {
    try {
      const response = await makeAuthenticatedRequest('/CustomPartners/types');
      // Backend returns { success, message, data } wrapper
      return response?.data || [];
    } catch (error) {
      console.error('Error fetching partner types:', error);
      // Return default types if API call fails
      return [
        { value: 'sponsor', label: 'Sponsor', icon: '', color: '#10b981' },
        { value: 'supplier', label: 'Lieferant', icon: '', color: '#3b82f6' },
        { value: 'media', label: 'Medien', icon: '', color: '#ef4444' },
        { value: 'venue', label: 'Veranstaltungsort', icon: '', color: '#f59e0b' },
        { value: 'service', label: 'Dienstleister', icon: '', color: '#06b6d4' },
        { value: 'charity', label: 'Wohltätigkeit', icon: '', color: '#84cc16' },
        { value: 'technology', label: 'Technologie', icon: '', color: '#8b5cf6' },
        { value: 'other', label: 'Sonstiges', icon: '', color: '#6b7280' },
      ];
    }
  },

  // Check if partner can be deleted (has no dependencies)
  canDelete: async (id: number): Promise<boolean> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/${id}/can-delete`);
      // Backend returns { success, message, data } wrapper
      return response?.data?.canDelete ?? true;
    } catch (error) {
      console.error('Error checking if partner can be deleted:', error);
      // Default to true if we can't check
      return true;
    }
  },

  // Search partners
  search: async (query: string, includeInactive = false): Promise<CustomPartner[]> => {
    try {
      const response = await makeAuthenticatedRequest(`/CustomPartners/search?q=${encodeURIComponent(query)}&includeInactive=${includeInactive}`);
      // Backend returns { success, message, data } wrapper
      return response?.data || [];
    } catch (error) {
      console.error('Error searching custom partners:', error);
      throw error;
    }
  },

  // Get partner statistics
  getStats: async (): Promise<{
    total: number;
    active: number;
    inactive: number;
    byType: Record<string, number>;
  }> => {
    try {
      const response = await makeAuthenticatedRequest('/CustomPartners/stats');
      // Backend returns { success, message, data } wrapper
      return response?.data || { total: 0, active: 0, inactive: 0, byType: {} };
    } catch (error) {
      console.error('Error fetching partner statistics:', error);
      return { total: 0, active: 0, inactive: 0, byType: {} };
    }
  },
};

// Export individual functions for easier importing
export const {
  getAll: getAllCustomPartners,
  getById: getCustomPartnerById,
  getByType: getCustomPartnersByType,
  create: createCustomPartner,
  update: updateCustomPartner,
  delete: deleteCustomPartner,
  toggleActive: toggleCustomPartnerActive,
  getPartnerTypes: getCustomPartnerTypes,
  canDelete: canDeleteCustomPartner,
  search: searchCustomPartners,
  getStats: getCustomPartnerStats,
} = customPartnersApi;

export default customPartnersApi;
